/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA ;
 */

/**
 * @typedef {Object} AdditionalErrorInfo
 * @property {Boolean} unhandledPromiseRejection - error was caught by window 'unhandledrejection' listener
 * @property {Boolean} edgeReadAloud - try to check if "Read aloud" tool of Edge browser was used on the page.
 * @property {Boolean} fromSvelte - can't be detected for certain! Just some DOM modification errors which were noticed to come from svelte in existing error logs
 */
/**
 * Add `additionalInfo: AdditionalErrorInfo` property to the error, about environment it happened in.
 * @param {Error|*} err
 * @param {Object?} options
 */
export function setAdditionalErrorInfo(err, options) {
    if (err instanceof Error) {
        const additionalInfo = {};

        if (options?.unhandledPromiseRejection) {
            additionalInfo.unhandledPromiseRejection = true;
        }

        const msg = (typeof err.message === 'string' ? err.message : '').toLowerCase();
        //cases seen so far, thrown by `node_modules/svelte/internal/index.mjs`:
        //  - DOMException/Error: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.
        //  - TypeError: Cannot read properties of null (reading 'insertBefore')
        //  - TypeError: Cannot read properties of undefined (reading 'forEach')
        const maybeSvelteException =
            msg.includes('insertbefore') && (msg.includes('cannot read properties of') || msg.includes('node'));
        //a) <msreadout> elements are added while reading, but they are removed after reading finishes.
        //b) <style> tag containing 'msreadout-line-highlight/msreadout-word-highlight' styles
        //   are added to the page once user toggles the tool, and are not removed after reading finishes
        const maybeEdgeReadAloud =
            !!document.querySelector('msreadoutspan') ||
            [...document.querySelectorAll('style')].some(i => i.innerHTML.includes('msreadout'));

        if (maybeEdgeReadAloud) {
            additionalInfo.edgeReadAloud = true;
        }
        if (maybeSvelteException) {
            additionalInfo.fromSvelte = true;
        }
        if (maybeEdgeReadAloud && maybeSvelteException) {
            err.recoverable = true;
        }

        if (Object.keys(additionalInfo).length) {
            err.additionalInfo = additionalInfo;
        }
    }
}
